import React, { Fragment, ReactElement, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

// Components

import AddressSummaryOfPerson from './pageIncludes/summary/AddressSummaryOfPerson';
import ApplyingForHealthCoverageSummary from './pageIncludes/summary/ApplyingForHealthCoverageSummary';
import PageHeader from '../headers/PageHeader';
import PrimaryContactPersonSummary from '@components/ssap/pages/pageIncludes/summary/PrimaryContactPersonSummary';
import RemovedMembersSummary from './pageIncludes/summary/summaryIncludes/RemovedMembersSummary';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// Action creators

import { fetchEnrollments } from '@actions/ssap/ssapActionCreators';

// Selectors

import {
    selectAddressVisibilityDueToGeneralDelivery,
    selectIfLivingArrangement,
    selectIfShouldFetchEnrollments,
    selectNonSeekingForCoverageAndEnrolled
} from '@reducers/appReducer';

// Constants

import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

// Types

import { ApplicationStore, SsapPage } from '@components/ssap/SsapTypes';

// Utils

import { isEmpty } from '@utils/miscellaneous/objectHelpers';


const Page1_07_E2 = (props: SsapPage): ReactElement => {

    const {
        data: {
            removedMembers = {},
            taxHousehold: {
                bloodRelationship,
                otherAddresses
            }
        },
        environment: {
            exchangeName,
            isState,
            stateCode
        },
        history,
        householdMembers,
        relations
    } = props;

    const {
        buttonContainer: ButtonContainer,
        itfFeatureEnabled
    } = props;


    const shouldFetchEnrollments = useSelector(selectIfShouldFetchEnrollments);
    const nonSeekingForCoverageAndEnrolled = useSelector(selectNonSeekingForCoverageAndEnrolled);
    const addressVisibilityDueToGeneralDelivery = useSelector(selectAddressVisibilityDueToGeneralDelivery);
    const livingArrangementEnabled = useSelector(selectIfLivingArrangement);

    useEffect(() => {
        shouldFetchEnrollments && props.actions.fetchEnrollments();
    }, [ ]);

    const isAtLeastOneEnrolled = nonSeekingForCoverageAndEnrolled.length > 0;

    return (
        <Fragment>
            <div id = 'saPrimaryContact' >
                <div className = 'subsection' >
                    <PageHeader
                        headerClassName = 'gi-heading-alt'
                        text = 'ssap.subheader.household_and_demo_information'
                    />

                    <div className = 'grid-row' >
                        <PrimaryContactPersonSummary />
                    </div>
                </div>
                <div className = 'subsection' >
                    <PageHeader
                        headerClassName = 'gi-heading-alt'
                        text = 'ssap.subheader.household_members'
                    />

                    <ValidationErrorMessage
                        isVisible = { isAtLeastOneEnrolled }
                        messageText = { VALIDATION_ERROR_MESSAGES.AT_LEAST_ONE_APPLICANT_ENROLLED }
                        messageTextReplacements = { { exchangeName } }
                        errorClasses={ [ 'margin-l-0', 'margin-b-10', 'width-full' ] }
                    />

                    <div className = 'grid-row' >
                        <ApplyingForHealthCoverageSummary
                            additionalElement = {
                                (
                                    <ButtonContainer
                                        args = { [ ] }
                                        customButtonClassName = 'float-right'
                                        type = "editButton_secondary"
                                    />
                                )
                            }
                            bloodRelationship = { bloodRelationship }
                            householdMembers = { householdMembers }
                            relations = { relations }
                        />
                    </div>

                    {
                        !isEmpty(removedMembers)
                            ? (
                                <RemovedMembersSummary
                                    { ...{
                                        ButtonContainer,
                                        history,
                                        removedMembers
                                    } }
                                />
                            )
                            : null
                    }

                    <PageHeader
                        headerClassName = 'gi-heading-alt'
                        text = 'ssap.subheader.household_members_addresses'
                    />

                    <AddressSummaryOfPerson
                        { ...{
                            addressVisibilityDueToGeneralDelivery,
                            ButtonContainer,
                            householdMembers,
                            isState,
                            itfFeatureEnabled,
                            livingArrangementEnabled,
                            otherAddresses,
                            stateCode
                        } }
                    />
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    environment: state.common.environment,

    data: state.ssap.data,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    relations: state.ssap.relations
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        fetchEnrollments
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page1_07_E2);
